import { useLayoutEffect, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { useToggle, useMeasure } from 'react-use';
import Marquee from 'react-fast-marquee';
import useSound from 'use-sound';
import { gsap } from 'gsap';
import { useMedia } from 'utils/hooks';

import Icon from 'components/atoms/Icon';

import playIcon from 'images/icons/tour-play-white.png';
import pauseIcon from 'images/icons/tour-pause-white.png';
import backward from 'images/icons/backward.png';
import forward from 'images/icons/forward.png';

import { Howl } from 'howler';

import styles from './index.css';

export interface AudioProp {
    className?: string;
    active?: boolean;
    trackName: string;
    src: string;
    onCloseComplete?: () => void;
}

const formatTime = (secs: number) => {
    const minutes = Math.floor(secs / 60);
    const returnedMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;

    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

    return `${returnedMinutes}:${returnedSeconds}`;
};

const Audio: React.FC<AudioProp> = ({
    className,
    active = true,
    trackName,
    src: srcUrl,
    onCloseComplete = () => {},
}) => {
    const [trackContainerRef, { width: trackContainerWidth }] = useMeasure<HTMLDivElement>();
    const [trackNameRef, { width: trackWidth }] = useMeasure<HTMLSpanElement>();

    const updateTimeRAF = useRef<number | null>(null);

    const mainTimeline = useRef<gsap.core.Timeline | null>(null);
    const leaveTimeline = useRef<gsap.core.Timeline | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    const { mediaScale } = useMedia();

    const audioSrc = useMemo(() => {
        if (srcUrl.includes('dropbox.com')) {
            return srcUrl.replace('www.dropbox.com', 'dl.dropbox.com');
        }

        return '';
    }, [srcUrl]);

    const [loaded, setLoaded] = useState(false);
    const [playing, togglePlayingState] = useToggle(false);
    const [currentTime, setCurrentTime] = useState(0);

    const soundRef = useRef<Howl | null>(null);

    const updateTime = useCallback(() => {
        if (soundRef.current) {
            const time = soundRef.current.seek() || 0;

            setCurrentTime(time);

            if (soundRef.current.playing()) {
                updateTimeRAF.current = requestAnimationFrame(updateTime);
            }
        }
    }, []);

    const [play, { pause, duration, stop, sound }] = useSound(audioSrc, {
        onload: () => {
            setLoaded(true);
        },
        onplay: () => {
            updateTime();
            togglePlayingState(true);
        },
        onseek: () => {
            if (soundRef.current) {
                if (!soundRef.current.playing()) {
                    const time = soundRef.current.seek() || 0;
                    setCurrentTime(time);
                }
            }
        },
        onpause: () => {
            togglePlayingState(false);
        },
        onstop: () => {
            togglePlayingState(false);
        },
        onend: () => {
            togglePlayingState(false);
        },
    });

    useLayoutEffect(() => {
        mainTimeline.current = gsap
            .timeline({})
            .fromTo(
                rootRef.current,
                { translateY: (window.innerWidth <= 899 ? -124 : -60) * mediaScale },
                { translateY: 0 },
            );

        leaveTimeline.current = gsap
            .timeline({
                paused: true,
                onComplete: onCloseComplete,
            })
            .to(rootRef.current, {
                translateY: (window.innerWidth <= 899 ? -124 : -60) * mediaScale,
            });

        return () => {
            mainTimeline.current?.kill();
            leaveTimeline.current?.kill();
        };
    }, []);

    useEffect(() => {
        soundRef.current = sound;
    }, [sound]);

    const toggleAudio = useCallback(() => {
        if (!playing) {
            play();
        } else {
            pause();
        }
    }, [playing, play, pause]);

    const playBackward = useCallback(() => {
        if (soundRef.current) {
            soundRef.current.seek(currentTime - 10);
        }
    }, [currentTime]);

    const playForward = useCallback(() => {
        if (soundRef.current) {
            soundRef.current.seek(currentTime + 10);
        }
    }, [currentTime]);

    useEffect(() => {
        if (updateTimeRAF.current) {
            cancelAnimationFrame(updateTimeRAF.current);
        }
    }, []);

    useEffect(() => {
        stop();
        setLoaded(false);
    }, [audioSrc]);

    useEffect(() => {
        if (!active) {
            stop();
            leaveTimeline.current?.play();
        }
    }, [active]);

    return (
        <div ref={rootRef} className={classnames(styles.audio, className)}>
            <div ref={trackContainerRef} className={styles.trackName}>
                <Marquee
                    className={trackWidth <= trackContainerWidth && styles.inactive}
                    play={trackWidth > trackContainerWidth}
                    gradient={false}
                >
                    <span ref={trackNameRef} className={styles.marqueeText}>
                        {trackName}
                    </span>
                </Marquee>
                <span
                    className={classnames(
                        styles.marqueeText,
                        trackWidth >= trackContainerWidth && styles.inactive,
                    )}
                >
                    {trackName}
                </span>
            </div>
            {loaded && (
                <div className={styles.actions}>
                    <button
                        type="button"
                        className={classnames(styles.backward, currentTime < 10 && styles.disabled)}
                        onClick={playBackward}
                    >
                        <img src={backward} alt="backward" />
                    </button>
                    <Icon
                        className={styles.play}
                        icon={{
                            default: playIcon,
                            hover: playIcon,
                        }}
                        activeIcon={{
                            default: pauseIcon,
                            hover: pauseIcon,
                        }}
                        active={playing}
                        onClick={toggleAudio}
                    />
                    <button
                        type="button"
                        className={classnames(
                            styles.forward,
                            (duration || 0) / 1000 - currentTime < 10 && styles.disabled,
                        )}
                        onClick={playForward}
                    >
                        <img src={forward} alt="forward" />
                    </button>
                </div>
            )}
            <div className={styles.time}>
                {formatTime(currentTime || 0)}/{formatTime((duration || 0) / 1000)}
            </div>
        </div>
    );
};

export default Audio;
